import React, { useEffect, useMemo, useState } from "react";
import { FaRegEdit, FaRegTrashAlt } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import apiKey from "../../../config/apiKey";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

/** ---------- Small UI helpers ---------- */
const SkeletonRow = () => (
  <tr className="animate-pulse">
    {[...Array(4)].map((_, i) => (
      <td key={i} className="py-4 px-1">
        <div className="h-4 w-32 bg-gray-200 rounded" />
      </td>
    ))}
  </tr>
);

const AcademicSubject = () => {
  const { t } = useTranslation();
  const admin = JSON.parse(localStorage.getItem("edo-admin"));

  /** ---------- Fetch professions (classes) ---------- */
  const [professions, setProfessions] = useState([]); // [{id, profession_name}]
  useEffect(() => {
    axios
      .get(`${apiKey}/api/edolutions/admin/classes`, {
        headers: { Authorization: `Bearer ${admin?.token}` },
      })
      .then((res) => {
        const mapped = (res.data || []).map((c) => ({
          id: c.id,
          profession_name: c.class_name, // map class_name -> profession_name for UI
        }));
        setProfessions(mapped);
      })
      .catch((err) => console.error(err));
  }, [admin?.token]);

  /** ---------- Create specialization ---------- */
  const [newSpecializationName, setNewSpecializationName] = useState("");
  const [selectedProfession, setSelectedProfession] = useState(""); // profession_id
  const [specializationId, setSpecializationId] = useState(""); // returned from create
  const [formError, setFormError] = useState("");

  const createSpecialization = async () => {
    if (!selectedProfession) {
      setFormError(t("adminDash.academicSubject.professionRequired"));
      return;
    }
    if (!newSpecializationName.trim()) {
      setFormError(t("adminDash.academicSubject.specializationRequired"));
      return;
    }
    setFormError("");

    try {
      // 1) create specialization (subject)
      const response = await axios.post(
        `${apiKey}/api/edolutions/admin/subject`,
        { subjects: newSpecializationName },
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      );

      const newId = response.data.subjectId;
      setSpecializationId(newId);

      toast.success(response.data.message || t("adminDash.academicSubject.createdSuccess"), {
        position: "bottom-right",
        autoClose: 4000,
        theme: "colored",
        style: { backgroundColor: "#2A3A54", color: "#fff" },
      });

      // 2) link specialization to profession (subjectclassespost)
      const linkRes = await axios.post(
        `${apiKey}/api/edolutions/admin/subjectclassespost`,
        { subject_id: newId, class_id: selectedProfession },
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      );

      toast.success(linkRes.data.message || t("adminDash.academicSubject.linkedSuccess"), {
        position: "bottom-right",
        autoClose: 4000,
        theme: "colored",
        style: { backgroundColor: "#2A3A54", color: "#fff" },
      });

      // Reset inputs
      setNewSpecializationName("");
      setSelectedProfession("");

      // Refresh table
      fetchLinks();
    } catch (error) {
      console.error("Error adding specialization:", error);
      toast.error(t("adminDash.academicSubject.addError"), {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
      });
      fetchLinks();
    }
  };

  /** ---------- Fetch profession–specialization links ---------- */
  const [rows, setRows] = useState([]); // [{ id, profession_name, specialization_name, subject_id, class_id }]
  const [fetchErr, setFetchErr] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchLinks = () => {
    setLoading(true);
    setFetchErr("");
    axios
      .get(`${apiKey}/api/edolutions/admin/subjectclassesget`, {
        headers: { Authorization: `Bearer ${admin?.token}` },
      })
      .then((res) => {
        const mapped = (res.data || []).map((r) => ({
          id: r.id,
          profession_name: r.class_name, // map class -> profession for UI
          specialization_name: r.subject_name, // map subject -> specialization for UI
          subject_id: r.subject_id,
          class_id: r.class_id,
        }));
        setRows(mapped);
      })
      .catch((error) => setFetchErr(error.message || t("adminDash.academicSubject.loadError")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.token]);

  /** ---------- Delete link ---------- */
  const deleteRow = (id) => {
    axios
      .delete(`${apiKey}/api/edolutions/admin/subjectclassesdelete/${id}`, {
        headers: { Authorization: `Bearer ${admin?.token}` },
      })
      .then((response) => {
        setRows((prev) => prev.filter((x) => x.id !== id));
        toast.success(response.data.message || t("adminDash.academicSubject.removedSuccess"), {
          position: "bottom-right",
          autoClose: 4000,
          theme: "colored",
          style: { backgroundColor: "#2A3A54", color: "#fff" },
        });
      })
      .catch((error) => {
        console.error("Error deleting:", error);
        toast.error(t("adminDash.academicSubject.deleteError"), {
          position: "bottom-right",
          autoClose: 5000,
          theme: "colored",
        });
      });
  };

  /** ---------- Update (edit) modal ---------- */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    id: null,
    profession_name: "",
    specialization_name: "",
    subject_id: "",
    class_id: "",
  });

  const openEdit = (item) => {
    setCurrentItem(item);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentItem({
      id: null,
      profession_name: "",
      specialization_name: "",
      subject_id: "",
      class_id: "",
    });
  };

  const updateItem = async () => {
    try {
      // 1) Update specialization (subject)
      const resp = await axios.put(
        `${apiKey}/api/edolutions/admin/subject/${currentItem.subject_id}`,
        { subjects: currentItem.specialization_name },
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      );

      const newSubjectId = resp.data.subjectId;
      setSpecializationId(newSubjectId);

      toast.success(resp.data.message || t("adminDash.academicSubject.updatedSuccess"), {
        position: "bottom-right",
        autoClose: 4000,
        theme: "colored",
        style: { backgroundColor: "#2A3A54", color: "#fff" },
      });

      // 2) Update link for profession–specialization
      const linkResp = await axios.put(
        `${apiKey}/api/edolutions/admin/subjectclassesupdate/${currentItem.id}`,
        { subject_id: newSubjectId, class_id: currentItem.class_id },
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      );

      toast.success(linkResp.data.message || t("adminDash.academicSubject.linkUpdatedSuccess"), {
        position: "bottom-right",
        autoClose: 4000,
        theme: "colored",
        style: { backgroundColor: "#2A3A54", color: "#fff" },
      });

      closeModal();
      fetchLinks();
    } catch (error) {
      console.error("Error updating specialization:", error);
      toast.error(t("adminDash.academicSubject.updateError"), {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
      });
      fetchLinks();
    }
  };

  /** ---------- Search + pagination ---------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filtered = useMemo(() => {
    const term = debouncedSearch.toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        (r.profession_name || "").toLowerCase().includes(term) ||
        (r.specialization_name || "").toLowerCase().includes(term)
    );
  }, [rows, debouncedSearch]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = Math.min((currentPage - 1) * itemsPerPage, totalItems);
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const pageSlice = useMemo(
    () => filtered.slice(startIndex, endIndex),
    [filtered, startIndex, endIndex]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, debouncedSearch]);

  const pageRange = useMemo(() => {
    const maxBtns = 5;
    if (totalPages <= maxBtns) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - (maxBtns - 1)));
    return Array.from({ length: maxBtns }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  return (
    <div className="relative my-8 max-sm:max-w-xs mx-auto sm:w-full flex flex-col min-w-0 mb-6 shadow-sm bg-white rounded-2xl border border-gray-200">
      {/* Add Specialization */}
      <div className="px-6 pt-6">
        <p className="text-xl font-bold">{t("adminDash.academicSubject.addTitle")}</p>
      </div>

      <div className="px-6 mb-2 flex max-sm:flex-col gap-4 mt-4">
        <div className="relative">
          <select
            value={selectedProfession}
            onChange={(e) => {
              setSelectedProfession(e.target.value);
              setFormError("");
            }}
            className="border shadow-sm rounded-md px-3 py-2 outline-none text-sm"
          >
            <option value="">{t("adminDash.academicSubject.selectProfession")}</option>
            {professions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.profession_name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-3 max-sm:flex-col sm:items-center w-full">
          <input
            type="text"
            className="border shadow-sm rounded-md px-3 py-2 outline-none text-sm w-full sm:w-80"
            placeholder={t("adminDash.academicSubject.specializationPlaceholder")}
            value={newSpecializationName}
            onChange={(e) => {
              setNewSpecializationName(e.target.value);
              setFormError("");
            }}
          />
          <button
            onClick={createSpecialization}
            className="bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:from-[#2A3A54] hover:to-[#EE5F2B] text-sm font-semibold rounded-md text-white px-5 py-2"
          >
            {t("adminDash.common.submit")}
          </button>
        </div>
      </div>

      {(formError || fetchErr) && (
        <div className="px-6">
          <p className="text-red-600 text-sm mb-2">{formError || fetchErr}</p>
        </div>
      )}

      {/* Toolbar */}
      <div className="px-6 py-4 flex flex-wrap items-center justify-between">
        <p className="text-lg font-semibold">{t("adminDash.academicSubject.listTitle")}</p>
        <div className="relative border rounded-md flex items-center px-4 py-2 shadow-sm">
          <FiSearch className="h-5 w-5 absolute left-2 text-gray-500" />
          <input
            type="text"
            className="outline-none pl-7 text-sm"
            placeholder={t("adminDash.academicSubject.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="mx-6 mb-6 overflow-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-50 text-[#6b7280] sticky top-0 z-10">
            <tr>
              <th className="py-3 px-2 font-medium w-1/12">{t("adminDash.common.sr")}</th>
              <th className="py-3 px-2 font-medium">{t("adminDash.academicSubject.colProfession")}</th>
              <th className="py-3 px-2 font-medium">{t("adminDash.academicSubject.colSpecialization")}</th>
              <th className="py-3 px-2 font-medium text-center w-2/12">{t("adminDash.common.actions")}</th>
            </tr>
          </thead>

          <tbody className="divide-y text-center">
            {loading && (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}

            {!loading && pageSlice.length === 0 && (
              <tr>
                <td colSpan={4} className="py-8 px-2 text-gray-500">
                  {t("adminDash.academicSubject.noData")}
                </td>
              </tr>
            )}

            {!loading &&
              pageSlice.map((item, index) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="py-4 px-2">{startIndex + index + 1}</td>
                  <td className="py-4 px-2 text-left">{item.profession_name}</td>
                  <td className="py-4 px-2 text-left">{item.specialization_name}</td>
                  <td className="py-4 px-2">
                    <div className="flex justify-center gap-6">
                      <button
                        onClick={() => openEdit(item)}
                        className="text-[#2A3A54] hover:opacity-80"
                        title={t("adminDash.common.edit")}
                      >
                        <FaRegEdit className="text-lg" />
                      </button>
                      <button
                        onClick={() => deleteRow(item.id)}
                        className="text-rose-600 hover:text-rose-700"
                        title={t("adminDash.common.delete")}
                      >
                        <FaRegTrashAlt className="text-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-wrap gap-5 justify-between items-center w-full text-sm px-6 pb-6">
        <div className="flex items-center gap-2 text-gray-600">
          <select
            className="text-sm border rounded-lg px-2 py-1 bg-white"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(parseInt(e.target.value, 10))}
          >
            {[10, 20, 30, 40, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span>{t("adminDash.common.showing")}</span>
          <span className="font-medium text-gray-800">
            {totalItems === 0 ? 0 : startIndex + 1}-{endIndex}
          </span>
          <span>{t("adminDash.common.of")} {totalItems} {t("adminDash.common.entries")}</span>
        </div>

        <div className="flex items-center gap-2 text-gray-600">
          <button
            className="text-base bg-gray-100 rounded-lg px-2 py-1 disabled:opacity-40"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label={t("adminDash.common.previousPage")}
          >
            <MdKeyboardArrowLeft />
          </button>

          {pageRange.map((p) => (
            <button
              key={p}
              className={`text-base rounded-lg px-3 py-1 ${
                p === currentPage ? "text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]" : "text-black bg-gray-100"
              }`}
              onClick={() => setCurrentPage(p)}
            >
              {p}
            </button>
          ))}

          <span>{t("adminDash.common.of")} {totalPages} {t("adminDash.common.pages")}</span>

          <button
            className="text-base bg-gray-100 rounded-lg px-2 py-1 disabled:opacity-40"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            aria-label={t("adminDash.common.nextPage")}
          >
            <MdKeyboardArrowRight />
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="px-5 py-4 border-b">
              <h2 className="text-lg font-semibold">{t("adminDash.academicSubject.updateTitle")}</h2>
            </div>
            <div className="px-5 py-5 space-y-4">
              <div>
                <label htmlFor="profession" className="block text-sm font-medium text-gray-700 mb-1">
                  {t("adminDash.academicSubject.professionLabel")}
                </label>
                <select
                  id="profession"
                  value={currentItem.class_id}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      class_id: e.target.value,
                    })
                  }
                  className="border shadow-sm rounded-md px-3 py-2 outline-none text-sm w-full"
                >
                  <option value="">{currentItem.profession_name || t("adminDash.academicSubject.selectProfession")}</option>
                  {professions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.profession_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="specialization" className="block text-sm font-medium text-gray-700 mb-1">
                  {t("adminDash.academicSubject.specializationLabel")}
                </label>
                <input
                  id="specialization"
                  type="text"
                  className="border p-2 w-full rounded-md shadow-sm"
                  value={currentItem.specialization_name}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      specialization_name: e.target.value,
                    })
                  }
                  placeholder={t("adminDash.academicSubject.specializationInputPlaceholder")}
                />
              </div>
            </div>

            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button
                onClick={closeModal}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm"
              >
                {t("adminDash.common.cancel")}
              </button>
              <button
                onClick={updateItem}
                className="bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:opacity-90 text-white px-4 py-2 rounded-lg text-sm"
              >
                {t("adminDash.common.update")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicSubject;
